const metaSelectInstances = new WeakMap();
let activeMetaSelectInstance = null;
let metaSelectSequence = 0;

const updateSimpleBar = (list) => {
  if (typeof SimpleBar === 'undefined') return;

  list.SimpleBar?.unMount();
  list.SimpleBar = new SimpleBar(list);

  ['.simplebar-track.simplebar-vertical', '.simplebar-track.simplebar-horizontal']
    .forEach((selector) => {
      Array.from(list.querySelectorAll(selector)).slice(1).forEach((track) => track.remove());
    });
};

const getLevenshteinDistance = (first, second) => {
  const matrix = Array.from({ length: second.length + 1 }, (_, index) => [index]);

  for (let index = 0; index <= first.length; index += 1) matrix[0][index] = index;

  for (let row = 1; row <= second.length; row += 1) {
    for (let column = 1; column <= first.length; column += 1) {
      matrix[row][column] = second[row - 1] === first[column - 1]
        ? matrix[row - 1][column - 1]
        : Math.min(
          matrix[row - 1][column - 1] + 1,
          matrix[row][column - 1] + 1,
          matrix[row - 1][column] + 1
        );
    }
  }

  return matrix[second.length][first.length];
};

class MetaSelectInstance {
  constructor(select) {
    this.select = select;
    this.container = select.closest('.meta-select');
    this.label = this.container?.querySelector('label');
    this.multiple = select.multiple;
    this.notInline = select.hasAttribute('data-meta-select-not-inline');
    this.isSearch = select.hasAttribute('data-meta-select-search');
    this.currentFocusIndex = -1;
    this.originalDisplay = select.style.display;
    this.sequence = metaSelectSequence += 1;

    this.wrapper = document.createElement('div');
    this.wrapper.className = 'meta-select-inner';
    this.wrapper.setAttribute('role', 'combobox');
    this.wrapper.setAttribute('aria-haspopup', 'listbox');
    this.wrapper.setAttribute('aria-expanded', 'false');
    this.wrapper.setAttribute('aria-disabled', String(select.disabled));
    this.wrapper.tabIndex = Number.isInteger(select.tabIndex) && select.tabIndex >= 0
      ? select.tabIndex
      : 0;

    this.placeholder = document.createElement('div');
    this.placeholder.className = 'meta-select-placeholder';

    this.list = document.createElement('div');
    this.list.className = 'meta-select-list';
    this.list.id = `meta-select-list-${this.sequence}`;
    this.list.setAttribute('role', 'listbox');
    this.list.setAttribute('aria-multiselectable', String(this.multiple));
    this.wrapper.setAttribute('aria-controls', this.list.id);
    const describedBy = select.getAttribute('aria-describedby');
    if (describedBy) this.wrapper.setAttribute('aria-describedby', describedBy);

    if (this.label) {
      this.label.id ||= `${this.list.id}-label`;
      this.wrapper.setAttribute('aria-labelledby', this.label.id);
    } else {
      this.wrapper.setAttribute('aria-label', select.getAttribute('aria-label') || 'Select option');
    }

    select.getAttribute('data-meta-select-list-class')?.split(' ')
      .filter(Boolean)
      .forEach((className) => this.list.classList.add(className));

    if (this.multiple) {
      this.list.classList.add('is-multiple');
      this.wrapper.classList.add('is-multiple');
    }

    if (this.isSearch) {
      this.searchInput = document.createElement('input');
      this.searchInput.className = 'meta-select-search';
      this.searchInput.type = 'search';
      this.searchInput.autocomplete = 'off';
      this.searchInput.setAttribute('aria-label', 'Search options');
      this.list.appendChild(this.searchInput);
      this.list.classList.add('is-search-input');
    }

    this.listContent = document.createElement('div');
    this.listContent.className = 'meta-select-list-content';
    this.list.appendChild(this.listContent);

    this.onWrapperClick = this.onWrapperClick.bind(this);
    this.onLabelClick = this.onLabelClick.bind(this);
    this.onDocumentClick = this.onDocumentClick.bind(this);
    this.onDocumentKeydown = this.onDocumentKeydown.bind(this);
    this.onTriggerKeydown = this.onTriggerKeydown.bind(this);
    this.onSelectChange = this.onSelectChange.bind(this);
    this.onSearchInput = this.onSearchInput.bind(this);
    this.onClearClick = this.onClearClick.bind(this);
    this.positionList = this.positionList.bind(this);
    this.onWindowScroll = this.onWindowScroll.bind(this);
    this.close = this.close.bind(this);

    this.wrapper.append(this.placeholder, this.list);
    this.container.appendChild(this.wrapper);
    this.select.style.display = 'none';
    this.select.setAttribute('aria-hidden', 'true');
    this.select.classList.add('is-init');

    this.wrapper.addEventListener('click', this.onWrapperClick);
    this.wrapper.addEventListener('keydown', this.onTriggerKeydown);
    this.label?.addEventListener('click', this.onLabelClick);
    this.select.addEventListener('change', this.onSelectChange);
    this.searchInput?.addEventListener('input', this.onSearchInput);
    document.addEventListener('click', this.onDocumentClick);

    this.refresh();
  }

  destroy() {
    this.close();
    this.wrapper.removeEventListener('click', this.onWrapperClick);
    this.wrapper.removeEventListener('keydown', this.onTriggerKeydown);
    this.label?.removeEventListener('click', this.onLabelClick);
    this.select.removeEventListener('change', this.onSelectChange);
    this.searchInput?.removeEventListener('input', this.onSearchInput);
    this.clearButton?.removeEventListener('click', this.onClearClick);
    document.removeEventListener('click', this.onDocumentClick);
    this.list.SimpleBar?.unMount();
    this.wrapper.remove();
    this.select.style.display = this.originalDisplay;
    this.select.removeAttribute('aria-hidden');
    this.select.classList.remove('is-init');
    metaSelectInstances.delete(this.select);
  }

  refresh() {
    this.multiple = this.select.multiple;
    this.list.classList.toggle('is-multiple', this.multiple);
    this.wrapper.classList.toggle('is-multiple', this.multiple);
    this.list.setAttribute('aria-multiselectable', String(this.multiple));
    this.renderItems();
    this.renderClearButton();
    this.applySearchFilter();
    this.sync();
    updateSimpleBar(this.list);

    if (this.isOpen()) this.positionList();
  }

  sync() {
    const selectedOptions = Array.from(this.select.options).filter((option) => {
      return option.selected && !this.isPlaceholder(option);
    });

    this.listContent.querySelectorAll('.meta-select-list__item').forEach((item) => {
      const option = Array.from(this.select.options).find((candidate) => candidate.value === item.dataset.value);
      if (!option) return;

      const selected = option.selected && !this.isPlaceholder(option);
      item.classList.toggle('is-selected', selected);
      item.classList.toggle('is-disabled', option.disabled || option.parentElement?.disabled);
      item.setAttribute('aria-selected', String(selected));
    });

    if (this.multiple) {
      this.placeholder.textContent = selectedOptions.length === 0
        ? this.placeholderOption()?.text || 'Selected...'
        : selectedOptions.length === 1
          ? selectedOptions[0].text
          : `Selected ${selectedOptions.length} items`;
    } else {
      this.placeholder.textContent = this.select.options[this.select.selectedIndex]?.text || 'Selected...';
    }

    this.container.classList.toggle('is-disabled-select', this.select.disabled);
    this.wrapper.setAttribute('aria-disabled', String(this.select.disabled));
    if (this.clearButton) this.clearButton.disabled = selectedOptions.length === 0;
    this.updateVisibility();
  }

  renderItems() {
    this.listContent.innerHTML = '';
    let optionIndex = 0;

    Array.from(this.select.children).forEach((child) => {
      if (child instanceof HTMLOptGroupElement) {
        const group = document.createElement('div');
        group.className = 'meta-select-list__group';

        const groupLabel = document.createElement('div');
        groupLabel.className = 'meta-select-list__group-label';
        groupLabel.textContent = child.label;
        group.appendChild(groupLabel);

        Array.from(child.children).forEach((option) => {
          if (this.isPlaceholder(option)) return;
          group.appendChild(this.createItem(option, child.disabled, optionIndex));
          optionIndex += 1;
        });

        if (group.querySelector('.meta-select-list__item')) this.listContent.appendChild(group);
        return;
      }

      if (child instanceof HTMLOptionElement) {
        if (this.isPlaceholder(child)) return;
        this.listContent.appendChild(this.createItem(child, false, optionIndex));
        optionIndex += 1;
      }
    });
  }

  createItem(option, groupDisabled, optionIndex) {
    const item = document.createElement('div');
    item.className = 'meta-select-list__item';
    item.id = `${this.list.id}-option-${optionIndex}`;
    item.dataset.value = option.value;
    item.setAttribute('role', 'option');

    if (this.isPlaceholder(option)) item.classList.add('is-placeholder');
    if (option.disabled || groupDisabled) item.classList.add('is-disabled');

    if (this.multiple) {
      const checkbox = document.createElement('span');
      checkbox.className = 'meta-select-list__checkbox';
      checkbox.setAttribute('aria-hidden', 'true');
      const text = document.createElement('span');
      text.className = 'meta-select-list__item-text';
      text.textContent = option.text;
      item.append(checkbox, text);
    } else {
      item.textContent = option.text;
    }

    item.addEventListener('click', () => {
      if (option.disabled || groupDisabled || this.isPlaceholder(option)) return;
      this.selectOption(option);
    });
    item.addEventListener('mouseenter', () => this.resetFocus());

    return item;
  }

  renderClearButton() {
    if (!this.multiple) {
      this.clearButton?.removeEventListener('click', this.onClearClick);
      this.clearButton?.remove();
      this.clearButton = null;
      return;
    }

    if (this.clearButton) return;

    this.clearButton = document.createElement('button');
    this.clearButton.className = 'meta-select-list__clear';
    this.clearButton.type = 'button';
    const clearIcon = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    clearIcon.classList.add('meta-select-list__clear-icon');
    clearIcon.setAttribute('viewBox', '0 0 24 24');
    clearIcon.setAttribute('aria-hidden', 'true');
    clearIcon.setAttribute('focusable', 'false');
    clearIcon.setAttribute('fill', 'none');
    clearIcon.setAttribute('stroke', 'currentColor');
    clearIcon.setAttribute('stroke-width', '1.8');
    clearIcon.setAttribute('stroke-linecap', 'round');
    clearIcon.setAttribute('stroke-linejoin', 'round');

    const clearIconPath = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    clearIconPath.setAttribute('d', 'M4 7h16m-10 4v6m4-6v6M9 7l1-3h4l1 3m-8 0 1 13h8l1-13');
    clearIcon.appendChild(clearIconPath);
    this.clearButton.append(clearIcon, 'Clear selection');
    this.clearButton.addEventListener('click', this.onClearClick);
    this.list.appendChild(this.clearButton);
  }

  onClearClick(event) {
    event.preventDefault();
    event.stopPropagation();

    Array.from(this.select.options).forEach((option) => {
      option.selected = false;
    });

    this.select.dispatchEvent(new Event('change', { bubbles: true }));
  }

  selectOption(option) {
    if (this.multiple) {
      Array.from(this.select.options).filter((candidate) => this.isPlaceholder(candidate))
        .forEach((candidate) => {
          candidate.selected = false;
        });
      option.selected = !option.selected;
    } else {
      this.select.value = option.value;
    }

    this.select.dispatchEvent(new Event('change', { bubbles: true }));
    if (!this.multiple) this.close();
  }

  onWrapperClick(event) {
    if (this.select.disabled || this.list.contains(event.target)) return;
    this.toggle();
  }

  onLabelClick(event) {
    if (this.select.disabled) return;
    event.preventDefault();
    event.stopPropagation();
    this.toggle();
  }

  onDocumentClick(event) {
    if (!this.wrapper.contains(event.target) && !this.list.contains(event.target) && !this.label?.contains(event.target)) {
      this.close();
    }
  }

  onTriggerKeydown(event) {
    if (this.isOpen()) return;

    if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(event.key)) {
      event.preventDefault();
      event.stopPropagation();
      this.open();
      this.keyboardNavigation = true;
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        this.moveFocus(event.key === 'ArrowDown' ? 1 : -1);
      }
    }
  }

  onDocumentKeydown(event) {
    if (!this.isOpen()) return;
    this.keyboardNavigation = true;

    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
      this.wrapper.focus();
      return;
    }

    if (event.target === this.searchInput && !['ArrowDown', 'ArrowUp'].includes(event.key)) return;

    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      this.moveFocus(event.key === 'ArrowDown' ? 1 : -1);
      return;
    }

    if ((event.key === 'Enter' || event.key === ' ') && this.currentFocusIndex >= 0) {
      event.preventDefault();
      this.visibleItems()[this.currentFocusIndex]?.click();
    }
  }

  onWindowScroll() {
    if (this.keyboardNavigation && (this.wrapper.contains(document.activeElement) || this.list.contains(document.activeElement))) {
      this.positionList();
    } else this.close();
  }

  onSelectChange() {
    this.sync();
  }

  onSearchInput() {
    this.applySearchFilter();
    this.resetFocus();
    this.list.SimpleBar?.recalculate();

    if (!this.select.hasAttribute('data-meta-select-nova')) return;

    window.clearTimeout(this.novaSearchTimer);
    this.novaSearchTimer = window.setTimeout(() => {
      const value = this.searchInput.value;
      if (value !== this.previousNovaSearchValue) {
        this.previousNovaSearchValue = value;
        window.startLoaderSelect?.();
      }
    }, 500);
  }

  applySearchFilter() {
    if (!this.isSearch) return;

    const searchTerm = this.searchInput.value.trim().toLowerCase();
    let visibleCount = 0;

    this.listContent.querySelectorAll('.meta-select-list__item').forEach((item) => {
      const optionText = item.textContent.toLowerCase();
      const visible = searchTerm === ''
        || optionText.includes(searchTerm)
        || getLevenshteinDistance(optionText, searchTerm) <= 1;
      item.hidden = !visible;
      visibleCount += Number(visible);
    });

    this.listContent.querySelectorAll('.meta-select-list__group').forEach((group) => {
      group.hidden = !Array.from(group.querySelectorAll('.meta-select-list__item')).some((item) => !item.hidden);
    });

    const noResults = this.listContent.querySelector('.meta-select-list__item-no-results');
    if (visibleCount === 0 && !noResults) {
      const message = document.createElement('span');
      message.className = 'meta-select-list__item-no-results';
      message.textContent = 'No results';
      this.listContent.appendChild(message);
    }

    if (visibleCount > 0) noResults?.remove();
  }

  updateVisibility() {
    const inputIds = new Set();
    Array.from(this.select.options).forEach((option) => {
      option.dataset.msShowFields?.split(',').forEach((id) => inputIds.add(id.trim()));
    });

    const selectedIds = new Set(
      Array.from(this.select.selectedOptions)
        .flatMap((option) => option.dataset.msShowFields?.split(',') || [])
        .map((id) => id.trim())
    );

    inputIds.forEach((id) => {
      const input = document.getElementById(id);
      if (!input) return;

      ['.meta-input', '.meta-select', '.meta-time-picker', '.meta-checkbox', '.meta-switch']
        .map((selector) => input.closest(selector))
        .filter(Boolean)
        .forEach((container) => container.classList.toggle('hidden', !selectedIds.has(id)));
    });
  }

  open() {
    if (this.select.disabled || this.isOpen()) return;
    this.keyboardNavigation = false;

    activeMetaSelectInstance?.close();
    activeMetaSelectInstance = this;
    this.list.classList.add('is-open');
    this.wrapper.classList.add('is-open');
    this.wrapper.setAttribute('aria-expanded', 'true');
    updateSimpleBar(this.list);
    this.positionList();
    window.addEventListener('resize', this.positionList);
    window.addEventListener('scroll', this.onWindowScroll, { passive: true });
    document.addEventListener('keydown', this.onDocumentKeydown);
    requestAnimationFrame(() => this.positionList());
    this.searchInput?.focus();
  }

  close() {
    if (!this.isOpen()) return;

    this.list.classList.remove('is-open');
    this.wrapper.classList.remove('is-open');
    this.wrapper.setAttribute('aria-expanded', 'false');
    window.removeEventListener('resize', this.positionList);
    window.removeEventListener('scroll', this.onWindowScroll);
    document.removeEventListener('keydown', this.onDocumentKeydown);

    if (this.notInline && this.list.parentElement !== this.wrapper) this.wrapper.appendChild(this.list);

    this.resetFocus();
    if (activeMetaSelectInstance === this) activeMetaSelectInstance = null;
  }

  toggle() {
    if (this.isOpen()) this.close();
    else this.open();
  }

  isOpen() {
    return this.list.classList.contains('is-open');
  }

  isPlaceholder(option) {
    return option.classList.contains('is-placeholder');
  }

  placeholderOption() {
    return Array.from(this.select.options).find((option) => this.isPlaceholder(option));
  }

  visibleItems() {
    return Array.from(this.listContent.querySelectorAll('.meta-select-list__item'))
      .filter((item) => !item.hidden && !item.classList.contains('is-disabled') && !item.classList.contains('is-placeholder'));
  }

  moveFocus(direction) {
    const items = this.visibleItems();
    if (!items.length) return;

    this.currentFocusIndex = (this.currentFocusIndex + direction + items.length) % items.length;
    items.forEach((item, index) => item.classList.toggle('is-focused', index === this.currentFocusIndex));
    this.wrapper.setAttribute('aria-activedescendant', items[this.currentFocusIndex].id);
    this.scrollFocusedItemIntoView(items[this.currentFocusIndex]);
  }

  resetFocus() {
    this.currentFocusIndex = -1;
    this.wrapper.removeAttribute('aria-activedescendant');
    this.listContent.querySelectorAll('.meta-select-list__item').forEach((item) => item.classList.remove('is-focused'));
  }

  scrollFocusedItemIntoView(item) {
    const scrollContainer = this.listContent.closest('.simplebar-content-wrapper');
    if (!scrollContainer || !item) return;

    const itemTop = item.offsetTop;
    const itemBottom = itemTop + item.offsetHeight;
    if (itemTop < scrollContainer.scrollTop) scrollContainer.scrollTop = itemTop - 8;
    else if (itemBottom > scrollContainer.scrollTop + scrollContainer.clientHeight) {
      scrollContainer.scrollTop = itemBottom - scrollContainer.clientHeight + 8;
    }
  }

  positionList() {
    if (!this.notInline || !this.isOpen()) return;

    if (this.list.parentElement !== document.body) document.body.appendChild(this.list);

    const wrapperRect = this.wrapper.getBoundingClientRect();
    this.list.style.position = 'fixed';
    this.list.style.minWidth = `${this.wrapper.clientWidth}px`;
    this.list.style.maxWidth = `${this.wrapper.clientWidth}px`;
    this.list.style.visibility = 'hidden';
    this.list.style.display = 'flex';
    this.list.style.top = '0';
    this.list.style.left = '0';
    this.list.style.maxHeight = '';
    this.list.style.height = '';

    const scrollContainer = this.list.querySelector('.simplebar-content-wrapper');
    scrollContainer?.style.removeProperty('height');
    scrollContainer?.style.removeProperty('max-height');

    const listHeight = this.list.offsetHeight;
    const bottomSpace = window.innerHeight - wrapperRect.bottom;
    const topSpace = wrapperRect.top;
    let top = wrapperRect.bottom + 3;

    if (bottomSpace < listHeight + 3 && topSpace >= listHeight + 3) {
      top = wrapperRect.top - listHeight - 3;
    } else if (bottomSpace < listHeight + 3) {
      const availableHeight = `${Math.max(bottomSpace - 10, 0)}px`;
      this.list.style.maxHeight = availableHeight;
      this.list.style.height = availableHeight;
      scrollContainer?.style.setProperty('height', availableHeight);
      scrollContainer?.style.setProperty('max-height', availableHeight);
    }

    this.list.style.top = `${top}px`;
    this.list.style.left = `${wrapperRect.left}px`;
    this.list.style.visibility = 'visible';
  }
}

const selectElements = (target) => {
  if (target instanceof HTMLSelectElement) return [target];
  if (target instanceof Element) return Array.from(target.querySelectorAll('[data-meta-select]'));
  return Array.from(document.querySelectorAll(target || '[data-meta-select]'));
};

window.resetMetaSelectFocus = (element) => {
  element?.querySelectorAll?.('.meta-select-list__item').forEach((item) => item.classList.remove('is-focused'));
};

window.initMetaSelect = (target = '[data-meta-select]') => {
  const selects = selectElements(target);

  selects.forEach((select, index) => {
    const container = select.closest('.meta-select');
    if (!container) return;

    container.style.zIndex = String(selects.length - index + 5);
    const instance = metaSelectInstances.get(select);
    if (instance) {
      instance.refresh();
      return;
    }

    metaSelectInstances.set(select, new MetaSelectInstance(select));
  });
};

window.destroyMetaSelect = (selectId) => {
  const select = document.getElementById(selectId);
  metaSelectInstances.get(select)?.destroy();
};

window.setMetaSelectValue = (selectId, value) => {
  const select = document.getElementById(selectId);
  if (!select) return;

  if (select.multiple) {
    const values = new Set((Array.isArray(value) ? value : [value]).map(String));
    Array.from(select.options).forEach((option) => {
      option.selected = values.has(option.value) && !option.classList.contains('is-placeholder');
    });
  } else if (Array.from(select.options).some((option) => option.value === String(value))) {
    select.value = String(value);
  } else {
    return;
  }

  select.dispatchEvent(new Event('change', { bubbles: true }));
};

window.updateMetaSelect = (selectId) => {
  const select = document.getElementById(selectId);
  if (select) window.initMetaSelect(select);
};

window.updateAllMetaSelects = () => window.initMetaSelect('[data-meta-select]');

window.initMetaSelect('[data-meta-select]');
